import {ALIGN, VALIGN} from '../globals';
import Sprite from '../display/Sprite';

var TextureCache = {};

let _LINES_DELIMITER = "\n";

/**
 * @class
 * @description Класс для рендеринга текста с использованием bitmap fonts.
 * <a href="http://71squared.com/en/product/2295/bitmap-font-and-pixel-font">http://71squared.com/en/product/2295/bitmap-font-and-pixel-font</a>
 *
 * @param {Object} config конфиг символов шрифта
 * @param {Object} asset ассет изображения
 * @extends module:display.Sprite
 * @memberOf module:display
 *
 * @example
 * Для подготовки шрифта рекомендуется использовать утилиту utils/fonts
 * С помощью флеш-части нужно подготовить и скачать zip со шрифтом
 * Затем распаковать в папку utils/fonts и используя кнопку Generate получить итоговый js-файл c charMap
 */
class BitmapText extends Sprite {

    constructor(config, asset) {
        super(null);

        this.config = config;
        this.asset = asset;

        this.frames = BitmapText.getFrames(config, asset);

        this._align = ALIGN.LEFT;
        this._valign = VALIGN.TOP;

        this._charSpacing = 0;
        this._lineSpacing = 0;
        this._lineHeight = null;

        this._maxWidth = 0;

        this._text = "";

        this.lines = [];
    }

    /** Разделитель строки. По умолчанию - \n */
    static get LINES_DELIMITER() {
        return _LINES_DELIMITER;
    }

    static set LINES_DELIMITER(val) {
        _LINES_DELIMITER = val;
    }

    /** выравнивание по горизонтали */
    get align() {
        return this._align;
    }

    set align(val) {
        this._align = val;
        this.refresh();
    }

    /** выравнивание по вертикали */
    get valign() {
        return this._valign;
    }

    set valign(val) {
        this._valign = val;
        this.refresh();
    }

    /** межсимвольное расстояние */
    get charSpacing() {
        return this._charSpacing;
    }

    set charSpacing(val) {
        this._charSpacing = val;
        this.refresh();
    }

    /** междустрочное расстояние */
    get lineSpacing() {
        return this._lineSpacing;
    }

    set lineSpacing(val) {
        this._lineSpacing = val;
        this.refresh();
    }

    /** фиксированная высота строки */
    get lineHeight() {
        return this._lineHeight;
    }

    set lineHeight(val) {
        this._lineHeight = val;
        this.refresh();
    }

    /** максимальная ширина, в которую должен вписаться текст */
    get maxWidth() {
        return this._maxWidth;
    }

    set maxWidth(val) {
        this._maxWidth = val;
        this.refresh();
    }

    /** отображаемый текст */
    get text() {
        return this._text;
    }

    set text(val) {
        this.write(val);
    }

    getChar(chr) {
        let id = chr.charCodeAt(0);

        for (let char of this.config) {
            if (char.id == id) return char;
        }

        console.log("Char not found", chr, id, this.text);
        return {id: 0, x: 0, y: 0, width: 0, height: 0, xoffset: 0, yoffset: 0, page: 0, xadvance: 0};
    }

    getCharIx(chr) {
        let id = chr.charCodeAt(0);

        for (let i = 0; i < this.config.length; i++) {
            if (this.config[i].id == id) return i;
        }

        console.log("Char not found", chr, id, this.text);
        return -1;
    }

    getWidth() {
        let width = 0;
        for (let line of this.lines) {
            let w = 0, chr;
            for (let j = 0; j < line.length; j++) {
                chr = this.getChar(line.substr(j, 1));
                w += chr.xadvance + this.charSpacing;
            }
            if (w > width) width = w;
        }

        return width;
    }

    getWidthOfLine(line) {
        let w = 0, chr;
        for (let i = 0; i < this.lines[line].length; i++) {
            chr = this.getChar(this.lines[line].substr(i, 1));
            w += chr.xadvance + this.charSpacing;
        }

        return w;
    }

    getHeight() {
        let h = 0;

        for (let i = 0; i < this.lines.length; i++) {
            h += this.getHeightOfLine(i) + this.lineSpacing;
        }
        return h;
    }

    getHeightOfLine(line) {
        if (this.lineHeight) return this.lineHeight;

        let h = 0, chr;
        for (let i = 0; i < this.lines[line].length; i++) {
            chr = this.getChar(this.lines[line].substr(i, 1));
            if (chr.height + chr.yoffset > h) h = chr.height + chr.yoffset;
        }
        
        if(this.lines[line].length == 0) {
            chr = this.getChar(" ");
            h = chr.height + chr.yoffset;
        }

        return h;
    }

    /** подгонка масштаба для вписывания текста в указанную ширину */
    setScaleToFitWidth(width, minScale, maxScale) {
        var newScale = width / this.getWidth();

        if (minScale && newScale < minScale) {
            newScale = minScale;
        }
        else if (maxScale && newScale > maxScale) {
            newScale = maxScale;
        }

        this.scale.set(newScale);
    }

    write(text) {
        let chr, chrIx, sprite, curX, curY, totalHeight, totalWidth, width, height, addSpace;

        this.removeChildren();

        text = text + "";

        if (this.maxWidth > 0) {
            var originalLines = text.split(BitmapText.LINES_DELIMITER);

            var lines = [], words, lineWords = [];
            for (var n = 0; n < originalLines.length; n++) {
                words = originalLines[n].split(" ");
                lineWords = [words[0]];
                for (let i = 1; i < words.length; i++) {
                    this.lines = [lineWords.join(" ") + " " + words[i]];
                    if (this.getWidthOfLine(0) > this.maxWidth) {
                        lines.push(lineWords.join(" "));
                        lineWords = ["  " + words[i]];
                    }
                    else lineWords.push(words[i]);
                }
                lines.push(lineWords.join(" "));
            }

            this.lines = lines;

            totalWidth = this.maxWidth;
        }
        else {
            this.lines = text.split(BitmapText.LINES_DELIMITER);
            totalWidth = this.getWidth();
        }

        this._text = text;

        curX = 0;
        curY = 0;

        totalHeight = this.getHeight();

        if (this.valign == VALIGN.MIDDLE) curY = -totalHeight / 2;
        if (this.valign == VALIGN.BOTTOM) curY = -totalHeight;

        var renderedHeight = 0;
        for (var i = 0; i < this.lines.length; i++) {
            height = this.getHeightOfLine(i);
            width = this.getWidthOfLine(i);

            curX = 0;
            addSpace = 0;

            if (this.align == ALIGN.CENTER) curX = -width / 2;
            if (this.align == ALIGN.RIGHT) curX = -width;

            if (this.align == ALIGN.JUSTIFY) {
                let words = this.lines[i].split(" ").length;
                if (words > 0) addSpace = (totalWidth - width) / (words - 1);
            }

            for (var j = 0; j < this.lines[i].length; j++) {

                chr = this.getChar(this.lines[i].substr(j, 1));
                chrIx = this.getCharIx(this.lines[i].substr(j, 1));

                if (chrIx >= 0) {

                    sprite = new PIXI.Sprite(this.frames[chrIx]);
                    sprite.position.set(curX + chr.xoffset, curY + chr.yoffset);
                    this.addChild(sprite);

                    curX += chr.xadvance + this.charSpacing;

                    if (chr.id == 32) curX += addSpace;
                }
            }

            curY += (this.lineHeight ? this.lineHeight : this.getHeightOfLine(i)) + this.lineSpacing;
        }
    }

    refresh() {
        this.write(this.text);
    }

    /** Получение списка текстур кадров для PIXI */
    static getFrames(config, asset) {
        let texture = PIXI.utils.TextureCache[asset.bitmap.src].baseTexture;
        if (!texture) {
            throw Error(`Texture ${asset.bitmap.src} not found`);
        }

        let frames = [];
        for (let chr of config) {
            let name = texture.imageUrl + "_" + chr.id;
            if (TextureCache[name]) {
                frames.push(TextureCache[name]);
            }
            else {
                let tex = new PIXI.Texture(texture);
                tex.frame = new PIXI.Rectangle(chr.x, chr.y, chr.width, chr.height);
                frames.push(tex);
                TextureCache[name] = tex;
            }
        }

        return frames;
    }
}

export default BitmapText;